import { useState, useRef, useEffect } from 'react';
import { Card, Box, Typography, Chip, Avatar, styled } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import ShareIcon from '@mui/icons-material/Share';

interface VideoAuthor {
    username: string;
    profile_url: string;
    verified: boolean;
    followers?: number;
}

interface VideoTag {
    name: string;
}

interface VideoItem {
    id: number;
    url: string;
    description: string;
    music_title: string;
    date: string;
    likes: number;
    comments: number;
    shares: number | null;
    bookmarks: number;
    views: number | null;
    author: VideoAuthor;
    tags: VideoTag[];
}

interface VideoCardProps {
    video: VideoItem;
}
const FlipCardContainer = styled(Box)({
    perspective: '1000px',
    width: '100%',
    height: '100%',
    minHeight: '400px',
});

const FlipCard = styled(Card)({
    position: 'relative',
    width: '100%',
    height: '100%',
    transition: 'transform 0.6s',
    transformStyle: 'preserve-3d',
    cursor: 'pointer',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
});

const CardFace = styled(Box)({
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    borderRadius: '12px',
    overflow: 'hidden',
});

const FrontFace = styled(CardFace)({
    backgroundColor: '#1a0933',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
});

const BackFace = styled(CardFace)({
    backgroundColor: '#0f051d',
    transform: 'rotateY(180deg)',
});

const VideoIframe = styled('iframe')({
    width: '100%',
    height: '100%',
    border: 'none',
});

export const VideoCard = ({ video }: VideoCardProps) => {
    const [isFlipped, setIsFlipped] = useState(false);
    const [iframeLoaded, setIframeLoaded] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const getTikTokEmbedUrl = (url: string) => {
        const videoId = url.split('/video/')[1];
        return `https://www.tiktok.com/embed/v2/${videoId}?autoplay=1`;
    };

    const handleMouseEnter = () => {
        setIsFlipped(true);
        // Устанавливаем src только при первом наведении
        if (iframeRef.current && !iframeRef.current.src) {
            iframeRef.current.src = `https://www.tiktok.com/embed/v2/${video.url.split('/video/')[1]}?autoplay=1`;
        }
    };

    const handleMouseLeave = () => {
        setIsFlipped(false);
    };

    useEffect(() => {
        if (isFlipped && !iframeLoaded) {
            const timer = setTimeout(() => {
                if (iframeRef.current) {
                    iframeRef.current.src = getTikTokEmbedUrl(video.url);
                    iframeRef.current.onload = () => setIframeLoaded(true);
                }
            }, 300); // Небольшая задержка для плавности анимации

            return () => clearTimeout(timer);
        }
    }, [isFlipped, iframeLoaded, video.url]);

    return (
        <FlipCardContainer>
            <FlipCard 
            // onMouseEnter={handleMouseEnter}
            //     onMouseLeave={handleMouseLeave}
                sx={{ transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0)' }}>
                {/* Передняя сторона */}
                <FrontFace>
                    <Box>
                        <Typography variant="h6" gutterBottom sx={{ color: '#ffffff' }}>
                            {video.description.substring(0, 60)}{video.description.length > 60 ? '...' : ''}
                        </Typography>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {video.tags.slice(0, 3).map((tag) => (
                                <Chip
                                    key={tag.name}
                                    label={tag.name}
                                    size="small"
                                    sx={{
                                        backgroundColor: 'rgba(110, 59, 255, 0.1)',
                                        color: '#6e3bff',
                                    }}
                                />
                            ))}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Avatar
                                src={video.author.profile_url}
                                alt={video.author.username}
                                sx={{ width: 32, height: 32 }}
                            />
                            <Typography variant="body2" sx={{ color: '#ffffff' }}>
                                @{video.author.username}
                                {video.author.verified && (
                                    <span style={{ color: '#1DA1F2', marginLeft: 4 }}>✓</span>
                                )}
                            </Typography>
                        </Box>
                    </Box>
{/* <StatItem>
                            <BookmarksIcon fontSize="small" />
                            <Typography variant="body2">
                                {video.bookmarks?.toLocaleString() || 'N/A'}
                            </Typography>
                        </StatItem>
                        <StatItem>
                            <InsertCommentIcon fontSize="small" />
                            <Typography variant="body2">
                                {video.comments?.toLocaleString() || 'N/A'}
                            </Typography>
                        </StatItem>

                        <StatItem>
                            <ThumbUpIcon fontSize="small" />
                            <Typography variant="body2">
                                {video.likes.toLocaleString()}
                            </Typography>
                        </StatItem>

                        <StatItem>
                            <ShareIcon fontSize="small" />
                            <Typography variant="body2">
                                {video.shares?.toLocaleString() || 'N/A'}
                            </Typography>
                        </StatItem> */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#b8b8b8' }}>
                            <BookmarksIcon fontSize="small" />
                            <Typography variant="body2">
                                {video.bookmarks?.toLocaleString() || 'N/A'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#b8b8b8' }}>
                            <InsertCommentIcon fontSize="small" />
                            <Typography variant="body2">
                                {video.comments.toLocaleString()}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#b8b8b8' }}>
                            <ThumbUpIcon fontSize="small" />
                            <Typography variant="body2">
                                {video.likes?.toLocaleString() || 'N/A'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#b8b8b8' }}>
                            <ShareIcon fontSize="small" />
                            <Typography variant="body2">
                                {video.shares?.toLocaleString() || 'N/A'}
                            </Typography>
                        </Box>
                    </Box>
                </FrontFace>

                {/* Задняя сторона с iframe */}
                <BackFace>
                   <p>sadasasdasd</p>
                </BackFace>
            </FlipCard>
        </FlipCardContainer>
    );
};